import { useEffect, useState } from 'react'
import { useFieldArray } from 'react-hook-form'
import type { Control } from 'react-hook-form/dist/types/form'
import {
  useEmailTemplateAttachments,
  useEmailTemplates
} from '~/api/queries/email'
import { Dropdown } from '~/components/Form'
import type { AttachmentField } from '~/components/Form/Input/AttachmentsInput'
import type { InputInFormProps } from '~/components/Form/Input/input'

type EmailTemplateSelectorProps = InputInFormProps & {
  name: string
  onChange?: () => void
  subjectFieldName?: string
  contentFieldName?: string
  control?: Control<{
    attachments: AttachmentField[]
  }>
}

export const EmailTemplateSelector = ({
  name,
  watch,
  register,
  control,
  setValue,
  onChange,
  subjectFieldName = 'subject',
  contentFieldName = 'message'
}: EmailTemplateSelectorProps) => {
  const [templateId, setTemplateId] = useState<number | null>(watch?.(name))

  const { data: templates, isLoading: templatesLoading } = useEmailTemplates()
  const { data: attachments } = useEmailTemplateAttachments(templateId)

  if (register !== undefined && name !== undefined) {
    register(name)
  }

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'attachments'
  })

  // Sync attachments fields with attachments from the template
  useEffect(() => {
    fields.map((field, index) => {
      if (
        field.fromTemplate &&
        !attachments?.find(
          (attachment) =>
            field.fromTemplate && attachment.object.id === field.object.id
        )
      ) {
        remove(index)
      }
    })
    if (attachments && attachments.length > 0) {
      for (const attachment of attachments) {
        if (
          !fields.find(
            (field) =>
              field.fromTemplate && field.object.id === attachment.object.id
          )
        ) {
          append({ fromTemplate: true, object: attachment.object })
        }
      }
    }
  }, [attachments, append, remove, fields])

  return (
    <Dropdown
      name="templateId"
      options={
        templates?.map((option) => ({
          label: option.name,
          value: String(option.id)
        })) ?? []
      }
      optionsLoading={templatesLoading}
      searchable
      defaultValue={templateId ? String(templateId) : undefined}
      onChange={(value: string) => {
        const valueId = Number.parseInt(value)
        setTemplateId(valueId)
        setValue?.(name, valueId)

        const selectedTemplate = templates?.find(
          (template) => template.id === valueId
        )

        if (subjectFieldName) {
          setValue?.(subjectFieldName, selectedTemplate?.subject ?? '')
        }

        const templateContent = selectedTemplate?.content ?? ''

        if (contentFieldName) {
          setValue?.(contentFieldName, templateContent)
        }
        onChange?.()
      }}
      label="Modèle"
      placeholder="Sélectionner un modèle"
    />
  )
}
